import React from 'react';
import classNames from 'classnames';
import {ExtensionType} from '../../../store/types/Extension';
import {calcExtNameFontSize, useStyles} from "./ExtensionPresenseWidget.utils";
import {useTranslation} from "react-i18next";
import {SipCall} from "../../../store/actions/ringgroups/payloads";

type ExtensionStatusKey =
    | 'unregistered'
    | 'idle'
    | 'onCall'
    | 'onHold'
    | 'ringing'
    | 'available'
    | 'loggedOut'
    | 'parked';

const statusOrder: ExtensionStatusKey[] = [
    'unregistered',
    'idle',
    'onCall',
    'onHold',
    'ringing',
    'available',
    'loggedOut',
    'parked'
];

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

function getExtensionStatus(
    e: ExtensionType,
    groups: Record<ExtensionStatusKey, ExtensionType[]>
): ExtensionStatusKey | null {
    return statusOrder.find((status) => groups[status]?.includes(e)) ?? null;
}

export const ExtensionStatusItem = (
    {
        e,
        darkMode,
        id,
        groups,
        sipCallsList
    }: {
        e: ExtensionType;
        darkMode: boolean;
        id: string;
        groups: Record<ExtensionStatusKey, ExtensionType[]>;
        sipCallsList?: SipCall[];
    }) => {

    const classes = useStyles();
    const {t} = useTranslation()
    const status = getExtensionStatus(e, groups);
    if (!status) return null;

    const iconClass = classNames(
        classes.listViewIconContainer,
        // @ts-ignore
        classes[`listViewIconContainer${capitalize(status)}`],
        // @ts-ignore
        darkMode && classes[`listViewIconContainer${capitalize(status)}DarkMode`]
    );

    const headerClass = classNames(
        classes.listViewRighHeader,
        // @ts-ignore
        classes[`listViewRighHeader${capitalize(status)}`],
        // @ts-ignore
        darkMode && classes[`listViewRighHeader${capitalize(status)}DarkMode`]
    );

    const labelClass = classNames(
        classes.statusDetails,
        // @ts-ignore
        classes[`statusDetails${capitalize(status)}`],
        // @ts-ignore
        darkMode && classes[`statusDetails${capitalize(status)}DarkMode`]
    );

    const duration = sipCallsList?.find(
        (d) =>
            d.callee?.extension_id === e.extension_id ||
            d.caller?.extension_id === e.extension_id
    )?.durationFormatSec;

    const labelMap: Record<ExtensionStatusKey, string> = {
        unregistered: t('screens:wallboard.unregisteredCount'),
        idle: t('screens:wallboard.idleCounts'),
        onCall: t('screens:wallboard.onCallCount'),
        onHold: t('screens:wallboard.onHoldCount'),
        ringing: t('screens:wallboard.ringingCount'),
        available: t('screens:wallboard.availableCount'),
        loggedOut: t('screens:wallboard.loggedOutCounts'),
        parked: t('screens:wallboard.parkedCounts')
    };

    return (
        <div className={classes.listViewItem} key={`widget_${id}_extension_${e.extension_id}`}>
            <div className={iconClass}>
                <div
                    className={classNames(classes.listViewIconText, darkMode && classes.listViewIconTextDarkMode)}
                    style={{fontSize: calcExtNameFontSize(e.extension_id)}}
                >
                    {e.extension_id}
                </div>
            </div>
            <div className={classes.listViewRighContainer}>
                <div className={headerClass}>{e.extension_name}</div>
                <div className={classes.listViewRighDetails}>
                    <div className={labelClass}>{labelMap[status]}</div>
                    {['onCall', 'onHold', 'ringing', 'parked'].includes(status) && duration && (
                        <div className={classNames(classes.callDuration, darkMode && classes.callDurationDarkMode)}>
                            {duration}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};